import axios, { AxiosError, AxiosRequestConfig } from "axios";
import JSONBig from "json-bigint";
import { Message } from "@arco-design/web-vue";
import { BaseResponse } from "@/types/common";

const NOT_LOGIN_CODE = 40100;
const LOGIN_PATH = "/user/login";
const LOGIN_STATUS_URL = "/user/get/login";
let redirectingToLogin = false;

const jsonBig = JSONBig({
  storeAsString: true,
});

const request = axios.create({
  // 生产环境由 Nginx 将同域 /api 转发至后端，避免浏览器把 localhost 误认为用户自己的电脑。
  baseURL: "/api",
  timeout: 20000,
  withCredentials: true,
  transformResponse: [
    (data) => {
      if (!data) {
        return data;
      }
      try {
        return jsonBig.parse(data);
      } catch (error) {
        return data;
      }
    },
  ],
});

const getRequestUrl = (config?: AxiosRequestConfig) => config?.url || "";

const isLoginStatusRequest = (url: string) => url.includes(LOGIN_STATUS_URL);
const isEmailCaptchaRequest = (url: string) => url.includes("/user/email/code");

const createRequestId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

request.interceptors.request.use((config) => {
  config.headers.set("X-Request-Id", createRequestId());
  return config;
});

const redirectToLogin = () => {
  if (redirectingToLogin || window.location.pathname === LOGIN_PATH) {
    return;
  }
  redirectingToLogin = true;
  Message.warning("\u8bf7\u5148\u767b\u5f55");
  const redirect = encodeURIComponent(
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
  window.location.href = `${LOGIN_PATH}?redirect=${redirect}`;
};

const redirectToLoginAndAbort = () => {
  redirectToLogin();
  return new Promise<never>(() => {
    // Keep the current request pending while the browser navigates away.
  });
};

request.interceptors.response.use(
  (response) => {
    const result = response.data as BaseResponse<unknown>;
    if (typeof result?.code === "number" && result.code !== 0) {
      const message = result.message || "\u8bf7\u6c42\u5931\u8d25";
      if (result.code === NOT_LOGIN_CODE) {
        const url = getRequestUrl(response.config);
        if (isLoginStatusRequest(url)) {
          return Promise.reject(new Error(message));
        }
        return redirectToLoginAndAbort();
      }
      // 图形验证码错误需要在验证弹窗内就地提示，避免用户被与操作无关的请求细节打扰。
      if (!isEmailCaptchaRequest(getRequestUrl(response.config))) {
        Message.error(message);
      }
      return Promise.reject(new Error(message));
    }
    return response;
  },
  (error: AxiosError<BaseResponse<unknown>>) => {
    const status = error.response?.status;
    const message =
      error.response?.data?.message ||
      error.message ||
      "\u7f51\u7edc\u5f02\u5e38";

    if (status === 401) {
      return redirectToLoginAndAbort();
    }

    if (!isEmailCaptchaRequest(getRequestUrl(error.config))) {
      Message.error(message);
    }
    return Promise.reject(error);
  }
);

export async function requestData<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await request.request<BaseResponse<T>>(config);
  return response.data.data;
}

export default request;
